import { AnalyticsEvent, EventGroup } from '../../../../types/working-model';
import { IDateOption } from '../../../general-ui/datepicker/date-range';

export function getMetricsQueryParams(search: string) {
	const queryString = new URLSearchParams(search);
	const startParam = queryString.get("start");
	const endParam = queryString.get("end");
	const audienceLists = queryString.getAll('audienceLists');
	const language = queryString.get('language');

	return {
		start: startParam ? parseInt(startParam || '') : undefined,
		end: endParam ? parseInt(endParam || '') : undefined,
		audienceLists: audienceLists,
		language: language ?? undefined,
	};
}

export function buildReportsURLString(
	end: IDateOption | undefined,
	start: IDateOption | undefined,
	timeZone: string,
	event: AnalyticsEvent,
	reportName: string,
	anonymizeSentimentData: boolean
) {
	const params = new URLSearchParams();
	end && params.set('endDate', new Date(end).getTime().toString());
	start && params.set('startDate', new Date(start).getTime().toString());
	params.set('channel', event.channel.toString());
	params.set('timeZone', timeZone);
	params.set('eventUuid', event.uuid);
	if (anonymizeSentimentData) {
		params.set('anonymizeSentimentData', 'true');
	}

	if (reportName === 'Registrations') {
		event.registration_settings?.marketing_email_options && params.set('marketingEmailOptions', event.registration_settings?.marketing_email_options);
	}

	return params.toString();
}

interface MetricParamsOptions {
	start?: Date | null;
	end?: Date | null;
	audienceList?: string[];
	language?: string;
	v2Dashboard?: boolean;
}

export const V1_DASHBOARD = 'v1-dashboard';
export function isDashboardV1() {
	return window.location.search.includes(V1_DASHBOARD);
}

export function generateMetricsFilterParams(options: MetricParamsOptions) {
	const { start, end, audienceList, language } = options;

	const params = new URLSearchParams();
	if (start) {
		params.set('start', start.valueOf().toString());
	}

	if (end) {
		params.set('end', end.valueOf().toString());
	}

	if (audienceList?.length) {
		audienceList?.forEach(list => params.append('audienceLists', list));
	}

	if (language?.length) {
		params.set('language', language);
	}

	const v1Dashboard = isDashboardV1();
	if (v1Dashboard) {
		params.set(V1_DASHBOARD, 'true');
	}

	const urlParams = params.toString();

	return urlParams ? `?${urlParams}` : '';
}

export const getQueryDates = (start: IDateOption | string | number | null, end: IDateOption | string | number | null, channelEvents: AnalyticsEvent[], eventUUID: string | undefined): { start: Date, end?: Date; } => {
	// channel events are sorted DESC so the last in the array is the first created
	// find first created event that has a registered user - the getEvents time range query filters by user session activity
	const firstAttendedEvent = [...channelEvents].reverse().find(event => event.registrations > 0);
	const firstEventStart = firstAttendedEvent ? new Date(firstAttendedEvent.created_date).valueOf() : Date.now();

	const getQueryStart = (): number => {
		// at event and session level, return latest of start prop or event creation date
		if (eventUUID) {
			const event = channelEvents.find(e => e.uuid === eventUUID);
			if (event?.created_date) {
				const eventStart = new Date(event.created_date).valueOf();
				return !!start && (Number(start) >= eventStart) ? Number(start) : eventStart;
			}
		}

		// at channel level, return latest of provided start prop or first event creation date

		return !!start && (Number(start) >= firstEventStart) ? Number(start) : firstEventStart;
	};

	let queryStart = getQueryStart();
	if (!end) {
		return {
			start: new Date(queryStart)
		};
	}

	let queryEnd = Number(end);

	// double check validity
	// start before end
	if (queryStart > queryEnd) {
		[queryStart, queryEnd] = [queryEnd, queryStart];
	}
	// a minimum 5 minute range
	const buffer = 1000 * 60 * 5;
	if (queryEnd - queryStart < buffer) {
		queryEnd = queryStart + buffer;
	}

	return { start: new Date(queryStart), end: queryEnd ? new Date(queryEnd) : undefined };
};

export function getEventGroups(eventGroups: EventGroup[], event_uuid: string): EventGroup[] {
	return eventGroups.filter(group => group.event_uuids?.includes(event_uuid));
}